export function Hero() {
  return (
    <section className="relative overflow-hidden py-20 sm:py-24 bg-[#1F2937]">
      <style jsx>{`
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        .cursor-blink {
          animation: blink 1s step-end infinite;
        }
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700&display=swap');
        .code-font {
          font-family: 'JetBrains Mono', monospace;
        }
        .code-block {
          background: var(--card-bg);
          border-radius: 8px;
          padding: 2.5rem;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
          border: 1px solid var(--border);
          position: relative;
        }
        .code-block::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 30px;
          background: var(--card-hover);
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
          border-bottom: 1px solid var(--border);
        }
        .code-line {
          color: var(--secondary);
        }
        .code-tag {
          color: #E06C75;
        }
        .code-content {
          color: #ABB2BF;
        }
        .line-number {
          color: #636D83;
          user-select: none;
        }
        .grid-bg {
          background-image: 
            linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
          background-size: 20px 20px;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        .float-animation {
          animation: float 3s ease-in-out infinite;
        }
        .button-glow {
          position: relative;
          overflow: hidden;
        }
        .button-glow::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
          transform: rotate(45deg);
          transition: transform 0.5s ease;
        }
        .button-glow:hover::before {
          transform: rotate(45deg) translate(50%, 50%);
        }
      `}</style>
      <div className="absolute inset-0 z-0 grid-bg"></div>
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0969DA]/5 to-[#2188FF]/5"></div>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="lg:pr-8">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight">
              <span className="block bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                FirstCommit
              </span>
              <div className="mt-8">
                <div className="code-block bg-[#282C34] border-[#3E4451]">
                  <div className="code-font text-lg sm:text-xl md:text-2xl">
                    <div className="flex">
                      <span className="line-number pr-6">1</span>
                      <span className="code-tag">&lt;code&gt;</span>
                    </div>
                    <div className="flex">
                      <span className="line-number pr-6">2</span>
                      <span className="code-content pl-4">
                        Your AI onboarding buddy — accelerate your developer onboarding, join teams confidently.
                        <span className="inline-block w-0.5 h-6 ml-1 bg-[#2188FF] cursor-blink"></span>
                      </span>
                    </div>
                    <div className="flex">
                      <span className="line-number pr-6">3</span>
                      <span className="code-tag">&lt;/code&gt;</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-8 flex gap-4">
                <a
                  href="https://calendly.com/zhangligao/first-commit-demo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group button-glow inline-flex items-center px-8 py-4 border border-transparent text-lg font-semibold rounded-xl text-white bg-gradient-to-r from-[#2188FF] to-[#0969DA] hover:from-[#0969DA] hover:to-[#2188FF] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 transform"
                >
                  <span className="mr-2">Book a Demo</span>
                  <svg 
                    className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M13 7l5 5m0 0l-5 5m5-5H6" 
                    />
                  </svg>
                </a>
              </div>
            </h1>
          </div>
          <div className="relative hidden lg:block">
            <div className="relative float-animation">
              <div className="absolute inset-0 bg-gradient-to-r from-[#0969DA]/10 to-[#2188FF]/10 rounded-lg blur-2xl"></div>
              <div className="relative rounded-lg shadow-xl w-full bg-white border border-[#D0D7DE] overflow-hidden">
                {/* GitHub comment header */}
                <div className="border-b border-[#D0D7DE] p-4 bg-[#F6F8FA]">
                  <div className="flex items-center space-x-3">
                    <img 
                      src="https://github.com/identicons/octocat.png" 
                      alt="Mentor" 
                      className="w-8 h-8 rounded-full"
                    />
                    <div>
                      <div className="text-[#24292F] font-mono text-sm">Sarah Chen</div>
                      <div className="text-[#57606A] font-mono text-xs">Senior Engineer @ GitHub</div>
                    </div>
                  </div>
                </div>
                
                {/* Comment content */}
                <div className="p-4 space-y-4 bg-white">
                  <div className="bg-[#F6F8FA] rounded-md p-4 border border-[#D0D7DE]">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 rounded-full bg-[#2DA44E] mt-2"></div>
                      </div>
                      <div className="flex-1">
                        <div className="text-[#24292F] font-mono text-sm">
                          Great progress on the onboarding flow! 🚀
                        </div>
                        <div className="text-[#57606A] font-mono text-xs mt-1">
                          The code structure is clean and well-organized. Keep up the good work!
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#F6F8FA] rounded-md p-4 border border-[#D0D7DE]">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 rounded-full bg-[#2DA44E] mt-2"></div>
                      </div>
                      <div className="flex-1">
                        <div className="text-[#24292F] font-mono text-sm">
                          Nice work on the API design! 💪
                        </div>
                        <div className="text-[#57606A] font-mono text-xs mt-1">
                          The RESTful endpoints are well-structured and the error handling is robust.
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#F6F8FA] rounded-md p-4 border border-[#D0D7DE]">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 rounded-full bg-[#2DA44E] mt-2"></div>
                      </div>
                      <div className="flex-1">
                        <div className="text-[#24292F] font-mono text-sm">
                          LGTM! Ready to merge. 🎉
                        </div>
                        <div className="text-[#57606A] font-mono text-xs mt-1">
                          The onboarding experience will be much smoother now.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Comment footer */}
                <div className="border-t border-[#D0D7DE] px-4 py-2 flex items-center justify-between bg-[#F6F8FA]">
                  <div className="flex items-center space-x-2">
                    <span className="text-[#57606A] font-mono text-xs">Just now</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button className="text-[#2188FF] font-mono text-xs hover:underline">Reply</button>
                    <button className="text-[#57606A] font-mono text-xs hover:underline">Resolve</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
} 